/**
 * Service centralisé pour gérer l'état des analyzers
 * Pattern : Appel API → Sauvegarde localStorage (par DID)
 */

const STORAGE_PREFIX = 'analyzer_';

class AnalyzerStateManager {
  constructor(analyzerApi) {
    this.api = analyzerApi;
  }

  /**
   * Clés standardisées pour localStorage
   */
  keys = {
    // État global (cache de l'API)
    state: (did) => `${STORAGE_PREFIX}state_${did}`,
    
    // Batch IDs par type
    batch: (platform, type, did) => `${STORAGE_PREFIX}${platform}_${type}_batch_${did}`,
    
    // Metadata temporaire
    meta: (platform, key, did) => `${STORAGE_PREFIX}${platform}_meta_${key}_${did}`,
  };

  /**
   * Résoudre l'état d'une plateforme
   * 1. Appeler API /analyzer/account/status
   * 2. Sauvegarder TOUT dans localStorage avec DID
   * 3. Retourner l'état de la plateforme
   */
  async resolveState(platform, issuerDid) {
    try {
      const response = await this.api.getAnalyzerAccountStatus(issuerDid);
      const apiData = response.data;

      this._saveState(issuerDid, apiData);

      const platformData = apiData[platform] || {};
      const uiState = this._determineUIState(platform, platformData);

      return {
        state: uiState,
        data: platformData,
        fromCache: false
      };
      
    } catch (error) {
      const localState = this._getLocalState(issuerDid);
      const platformData = localState[platform] || {};

      return {
        state: this._determineUIState(platform, platformData),
        data: platformData,
        fromCache: true,
        error: error.message
      };
    }
  }

  /**
   * Déterminer quel UI afficher selon les données
   */
  _determineUIState(platform, data) {
    if (!data) {
      return 'SETUP';
    }

    if (!data.configured) {
      return 'SETUP';
    }

    // ⚠️ RÈGLE : Si configured = true, vérifier d'abord le statut d'analyse
    // Si analyse EN COURS (PROGRESS), afficher l'étape 3 de chargement (polling)
    // Sinon, si analyse TERMINÉE (COMPLETE) ET hasData = false, afficher l'erreur
    
    if (platform === 'twitter') {
      // 1. Vérifier d'abord si analyse en cours
      if (data.globalBatchStatus === 'PROGRESS') return 'DASHBOARD_POLLING_GLOBAL';
      if (data.batchStatus === 'PROGRESS') return 'DASHBOARD_POLLING';
      
      // 2. Si analyse terminée, vérifier hasData
      if (data.hasData === false) {
        return 'SETUP_ERROR';
      }

      return 'DASHBOARD';
    }

    if (platform === 'linkedin') {
      if (data.batchStatus === 'PROGRESS') return 'DASHBOARD_POLLING';
      if (data.hasData === false) {
        return 'SETUP_ERROR';
      }
      
      // 3. Sinon, afficher le dashboard
      return 'DASHBOARD';
    }

    if (platform === 'discord') {
      if (['PROGRESS', 'PENDING'].includes(String(data.wizardBatchStatus || '').toUpperCase())) return 'WIZARD_POLLING';
      if (['PROGRESS', 'PENDING'].includes(String(data.batchStatus || '').toUpperCase())) return 'DASHBOARD_POLLING';
      if (data.channels?.length > 0) return 'DASHBOARD';
      return 'SETUP';
    }

    if (platform === 'telegram') {
      if (['PROGRESS', 'PENDING'].includes(String(data.wizardBatchStatus || '').toUpperCase())) return 'WIZARD_POLLING';
      if (['PROGRESS', 'PENDING'].includes(String(data.batchStatus || '').toUpperCase())) return 'DASHBOARD_POLLING';
      if (data.configured) return 'DASHBOARD';
      return 'SETUP';
    }

    if (platform === 'mentions') {
      if (data.batchStatus === 'PROGRESS') return 'DASHBOARD_POLLING';
      return 'DASHBOARD';
    }

    return 'DASHBOARD';
  }

  /**
   * Sauvegarder un batch en cours
   */
  saveBatch(platform, type, batchId, metadata, issuerDid) {
    const key = this.keys.batch(platform, type, issuerDid);
    const data = {
      batchId,
      metadata,
      startedAt: Date.now(),
      status: 'PROGRESS'
    };
    
    localStorage.setItem(key, JSON.stringify(data));

    // Mettre à jour aussi l'état global
    this._updateStateCache(platform, type, batchId, 'PROGRESS', issuerDid);
  }

  /**
   * Nettoyer un batch terminé
   */
  clearBatch(platform, type, issuerDid) {
    const key = this.keys.batch(platform, type, issuerDid);
    localStorage.removeItem(key);

    // Nettoyer aussi les metadata associées
    this._clearMetadata(platform, issuerDid);
    
    // Mettre à jour l'état global
    this._updateStateCache(platform, type, null, null, issuerDid);
  }

  /**
   * Sauvegarder metadata temporaire
   */
  saveMeta(platform, key, value, issuerDid) {
    const storageKey = this.keys.meta(platform, key, issuerDid);
    localStorage.setItem(storageKey, value);
  }

  /**
   * Récupérer metadata
   */
  getMeta(platform, key, issuerDid) {
    const storageKey = this.keys.meta(platform, key, issuerDid);
    return localStorage.getItem(storageKey);
  }

  /**
   * Nettoyer toutes les metadata d'une plateforme
   */
  _clearMetadata(platform, issuerDid) {
    const prefix = `${STORAGE_PREFIX}${platform}_meta_`;
    Object.keys(localStorage)
      .filter(key => key.startsWith(prefix) && key.endsWith(issuerDid))
      .forEach(key => localStorage.removeItem(key));
  }

  /**
   * Récupérer l'état local complet
   */
  _getLocalState(issuerDid) {
    const key = this.keys.state(issuerDid);
    const cached = localStorage.getItem(key);
    
    if (!cached) {
      return {};
    }

    try {
      const parsed = JSON.parse(cached);
      return parsed;
    } catch (error) {
      return {};
    }
  }

  /**
   * Sauvegarder l'état complet en cache
   * ÉCRASE complètement l'ancien cache pour ce DID
   */
  _saveState(issuerDid, state) {
    const key = this.keys.state(issuerDid);
    const data = {
      ...state,
      _cachedAt: Date.now(),
      _issuerDid: issuerDid
    };
    
    localStorage.setItem(key, JSON.stringify(data));
  }

  /**
   * Mettre à jour un batch dans le cache
   */
  _updateStateCache(platform, type, batchId, status, issuerDid) {
    const state = this._getLocalState(issuerDid);
    
    if (!state[platform]) {
      state[platform] = {};
    }

    // Mettre à jour selon le type
    if (type === 'global') {
      state[platform].globalBatchId = batchId;
      state[platform].globalBatchStatus = status;
    } else if (type === 'wizard') {
      state[platform].wizardBatchId = batchId;
      state[platform].wizardBatchStatus = status;
    } else {
      state[platform].batchId = batchId;
      state[platform].batchStatus = status;
    }

    // Ajouter le timestamp et DID
    state._cachedAt = Date.now();
    state._issuerDid = issuerDid;

    this._saveState(issuerDid, state);
  }

  /**
   * Forcer une synchronisation complète
   */
  async forceSync(issuerDid) {
    try {
      const response = await this.api.getAnalyzerAccountStatus(issuerDid);
      this._saveState(issuerDid, response.data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Nettoyer l'état et les meta d'une plateforme pour un issuer (ex. après reset liaison Telegram).
   * Au prochain resolveState, le cache ne contiendra plus cette plateforme (fallback si API en erreur).
   */
  clearPlatformState(platform, issuerDid) {
    const state = this._getLocalState(issuerDid);
    if (Object.keys(state).length > 0) {
      state[platform] = {};
      this._saveState(issuerDid, state);
    }
    this._clearMetadata(platform, issuerDid);
  }

  /**
   * Nettoyer tout le localStorage pour un issuer
   */
  clearAll(issuerDid) {
    Object.keys(localStorage)
      .filter(key => key.startsWith(STORAGE_PREFIX) && key.includes(issuerDid))
      .forEach(key => localStorage.removeItem(key));
  }
}

export default AnalyzerStateManager;
